import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, ShoppingCart, Package, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";

const FAB_SIZE = 56;

export function QuickActionFAB() {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const { settings } = useEmployeeViewSettings();
  const pathname = location.pathname;
  const isEmployee = role === "employee";

  const hideFAB =
    pathname === "/pricing" ||
    pathname === "/inventory" ||
    pathname.startsWith("/admin") ||
    pathname === "/login" ||
    pathname.startsWith("/auth/") ||
    pathname.startsWith("/settings");

  const handleRecordSale = useCallback(() => {
    setExpanded(false);
    navigate("/manual-entry");
  }, [navigate]);

  const handleAddStock = useCallback(() => {
    setExpanded(false);
    navigate(isEmployee ? "/?openAdd=1" : "/inventory?openAdd=1");
  }, [isEmployee, navigate]);

  const handleLogExpense = useCallback(() => {
    setExpanded(false);
    navigate("/expenses?openAdd=1");
  }, [navigate]);

  if (hideFAB) return null;

  const canRecordSale = !isEmployee || settings.allowEmployeeRecordSales;
  const canAddStock = !isEmployee || settings.allowEmployeeAddInventoryItems;
  const canLogExpense = !isEmployee || settings.allowEmployeeRecordExpenses;

  if (!canRecordSale && !canAddStock && !canLogExpense) return null;

  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-50 flex flex-col items-end gap-3 pb-[env(safe-area-inset-bottom)] pointer-events-none">
      {/* Expanded actions — fan upward; unmount when collapsed so invisible buttons don't reserve layout space */}
      <div
        className={cn(
          "flex flex-col-reverse items-end gap-3 transition-all duration-200 pointer-events-auto",
          expanded ? "opacity-100 scale-100" : "hidden",
        )}
      >
        {canLogExpense && (
          <button
            type="button"
            onClick={handleLogExpense}
            className="flex items-center gap-2 rounded-full bg-card border border-border shadow-lg hover:bg-accent px-4 h-12 min-w-[48px] transition-colors"
            aria-label="Log expense"
          >
            <Receipt className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium whitespace-nowrap">Log Expense</span>
          </button>
        )}
        {canAddStock && (
          <button
            type="button"
            onClick={handleAddStock}
            className="flex items-center gap-2 rounded-full bg-card border border-border shadow-lg hover:bg-accent px-4 h-12 min-w-[48px] transition-colors"
            aria-label="Add stock"
          >
            <Package className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium whitespace-nowrap">Add Stock</span>
          </button>
        )}
        {canRecordSale && (
          <button
            type="button"
            onClick={handleRecordSale}
            className="flex items-center gap-2 rounded-full bg-card border border-border shadow-lg hover:bg-accent px-4 h-12 min-w-[48px] transition-colors"
            aria-label="Record sale"
          >
            <ShoppingCart className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium whitespace-nowrap">Record Sale</span>
          </button>
        )}
      </div>

      {/* Main FAB */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={cn(
          "pointer-events-auto rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-transform flex items-center justify-center",
          "w-14 h-14 sm:w-[56px] sm:h-[56px]"
        )}
        style={{ width: FAB_SIZE, height: FAB_SIZE, minWidth: FAB_SIZE, minHeight: FAB_SIZE }}
        aria-label={expanded ? "Close quick actions" : "Open quick actions"}
        aria-expanded={expanded}
      >
        <Plus
          className={cn("h-6 w-6 transition-transform duration-200", expanded && "rotate-45")}
        />
      </button>
    </div>
  );
}
